import { createContext, useCallback, useContext, useMemo, useState } from 'react'

export const THEME_STORAGE_KEY = 'lighter-theme'

function readStoredTheme() {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (value === 'light' || value === 'dark') return value
  } catch {
    /* private mode / blocked storage */
  }
  return 'dark'
}

function readDocumentTheme() {
  const value = document.documentElement.dataset.theme
  if (value === 'light' || value === 'dark') return value
  return readStoredTheme()
}

function applyTheme(theme) {
  document.documentElement.dataset.theme = theme
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    /* private mode / blocked storage */
  }
}

const ThemeContext = createContext(null)

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readDocumentTheme)

  const setTheme = useCallback((next) => {
    setThemeState((current) => {
      const resolved = typeof next === 'function' ? next(current) : next
      if (resolved !== 'light' && resolved !== 'dark') return current
      applyTheme(resolved)
      return resolved
    })
  }, [])

  const toggleTheme = useCallback(() => {
    setTheme((current) => (current === 'light' ? 'dark' : 'light'))
  }, [setTheme])

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return context
}
